// Select and Mask dialog — Photoshop's refinement workspace.
//
// UI:
//   - View Mode dropdown: Onion Skin / Marching Ants / Overlay / On Black /
//     On White / Black & White / On Layers
//   - Edge Detection section: Radius slider, Smart Radius checkbox
//   - Adjust Edge section: Smooth, Feather, Contrast, Shift Edge sliders
//   - Output Settings section: Decontaminate Colors checkbox, Output To
//     dropdown (Selection / Layer Mask / Layer / Selection + Layer Mask /
//     New Document / New Document with Layer Mask)
//   - Reset and Cancel/OK buttons
//
// The dialog is presentation only. It resolves with a SelectAndMaskResult
// describing the chosen settings; the caller applies them against the active
// selection via Selection.{smooth, feather, expand, contract} and the
// Selection-edge contrast routine.

import { Selection } from "../../core/selection/Selection";

export type SelectAndMaskViewMode =
  | "onion-skin"
  | "marching-ants"
  | "overlay"
  | "on-black"
  | "on-white"
  | "black-and-white"
  | "on-layers";

export type SelectAndMaskOutput =
  | "selection"
  | "layer-mask"
  | "layer"
  | "selection-and-layer-mask"
  | "new-document"
  | "new-document-with-layer-mask";

export interface SelectAndMaskSettings {
  viewMode: SelectAndMaskViewMode;
  /** Edge Detection Radius (0..250 px). */
  radius: number;
  /** Smart Radius checkbox. */
  smartRadius: boolean;
  /** Adjust Edge: Smooth (0..100). */
  smooth: number;
  /** Adjust Edge: Feather (0..250 px). */
  feather: number;
  /** Adjust Edge: Contrast (0..100). */
  contrast: number;
  /** Adjust Edge: Shift Edge (-100..100). */
  shiftEdge: number;
  /** Decontaminate Colors checkbox. */
  decontaminate: boolean;
  /** Decontaminate amount (0..100). */
  decontaminateAmount: number;
  /** Output target. */
  outputTo: SelectAndMaskOutput;
}

export const VIEW_MODES: ReadonlyArray<{ value: SelectAndMaskViewMode; label: string }> = [
  { value: "onion-skin", label: "Onion Skin" },
  { value: "marching-ants", label: "Marching Ants" },
  { value: "overlay", label: "Overlay" },
  { value: "on-black", label: "On Black" },
  { value: "on-white", label: "On White" },
  { value: "black-and-white", label: "Black & White" },
  { value: "on-layers", label: "On Layers" },
];

export const OUTPUT_TARGETS: ReadonlyArray<{ value: SelectAndMaskOutput; label: string }> = [
  { value: "selection", label: "Selection" },
  { value: "layer-mask", label: "Layer Mask" },
  { value: "layer", label: "Layer" },
  { value: "selection-and-layer-mask", label: "Selection + Layer Mask" },
  { value: "new-document", label: "New Document" },
  { value: "new-document-with-layer-mask", label: "New Document with Layer Mask" },
];

export function defaultSettings(): SelectAndMaskSettings {
  return {
    viewMode: "overlay",
    radius: 0,
    smartRadius: false,
    smooth: 0,
    feather: 0,
    contrast: 0,
    shiftEdge: 0,
    decontaminate: false,
    decontaminateAmount: 0,
    outputTo: "selection",
  };
}

/**
 * Apply Adjust Edge sliders to a Selection. Pure function — works in core,
 * but also re-exported here for the dialog and for tests that need the same
 * math as the live preview.
 *
 * NOTE: This is the canonical implementation of the Select-and-Mask edge
 * adjustment math. Other callers (legacy `selectAndMask` in main.ts) should
 * migrate to this.
 */
export function applyEdgeAdjustments(
  sel: Selection,
  s: Pick<SelectAndMaskSettings, "smooth" | "feather" | "contrast" | "shiftEdge">,
): Selection {
  let out = sel;
  if (s.smooth > 0) out = out.smooth(s.smooth);
  if (s.feather > 0) out = out.feather(s.feather);
  if (s.shiftEdge > 0) out = out.expand(s.shiftEdge);
  else if (s.shiftEdge < 0) out = out.contract(-s.shiftEdge);
  if (s.contrast > 0) {
    const factor = 1 + s.contrast / 50; // 0..100 → 1..3
    const next = new Uint8Array(out.mask.length);
    for (let i = 0; i < out.mask.length; i++) {
      const v = (out.mask[i] ?? 0) / 255;
      const c = Math.min(1, Math.max(0, (v - 0.5) * factor + 0.5));
      next[i] = Math.round(c * 255);
    }
    out = new Selection(out.docW, out.docH, next);
  }
  return out;
}

export interface SelectAndMaskResult {
  settings: SelectAndMaskSettings;
}

/** Dialog class. */
export class SelectAndMaskDialog {
  private overlay: HTMLDivElement | null = null;
  private resolve: ((result: SelectAndMaskResult | null) => void) | null = null;
  private settings: SelectAndMaskSettings = defaultSettings();

  open(initial?: Partial<SelectAndMaskSettings>): Promise<SelectAndMaskResult | null> {
    this.close();
    this.settings = { ...defaultSettings(), ...initial };

    return new Promise<SelectAndMaskResult | null>((resolve) => {
      this.resolve = resolve;

      const overlay = document.createElement("div");
      overlay.className = "filter-dialog select-and-mask-dialog";
      // ARIA: modal dialog accessibility (#47)
      overlay.setAttribute("role", "dialog");
      overlay.setAttribute("aria-modal", "true");
      overlay.setAttribute("aria-labelledby", "select-mask-dialog-title");
      overlay.innerHTML = `
        <div class="filter-dialog__sheet" style="max-width:520px">
          <div class="filter-dialog__title" id="select-mask-dialog-title">Select and Mask</div>
          <div class="filter-dialog__body" style="display:flex;flex-direction:column;gap:8px;padding:12px">

            <!-- View Mode -->
            <div class="filter-dialog__section" data-section="view-mode">
              <div class="filter-dialog__section-title">View Mode</div>
              <div class="filter-dialog__row">
                <label class="filter-dialog__label">View:</label>
                <select class="filter-dialog__select" data-field="viewMode">
                  ${VIEW_MODES.map((m) =>
                    `<option value="${m.value}" ${m.value === this.settings.viewMode ? "selected" : ""}>${m.label}</option>`,
                  ).join("")}
                </select>
              </div>
            </div>

            <!-- Edge Detection (TODO: Smart Radius preview path is a follow-up.) -->
            <div class="filter-dialog__section" data-section="edge-detection">
              <div class="filter-dialog__section-title">Edge Detection</div>
              <div class="filter-dialog__row">
                <label class="filter-dialog__label">Radius:</label>
                ${slider("radius", this.settings.radius, 0, 250, 1, "px")}
              </div>
              <div class="filter-dialog__row">
                <label class="filter-dialog__label">
                  <input type="checkbox" data-field="smartRadius" ${this.settings.smartRadius ? "checked" : ""}>
                  Smart Radius
                </label>
              </div>
            </div>

            <!-- Adjust Edge -->
            <div class="filter-dialog__section" data-section="adjust-edge">
              <div class="filter-dialog__section-title">Adjust Edge</div>
              <div class="filter-dialog__row">
                <label class="filter-dialog__label">Smooth:</label>
                ${slider("smooth", this.settings.smooth, 0, 100, 1, "")}
              </div>
              <div class="filter-dialog__row">
                <label class="filter-dialog__label">Feather:</label>
                ${slider("feather", this.settings.feather, 0, 250, 0.1, "px")}
              </div>
              <div class="filter-dialog__row">
                <label class="filter-dialog__label">Contrast:</label>
                ${slider("contrast", this.settings.contrast, 0, 100, 1, "%")}
              </div>
              <div class="filter-dialog__row">
                <label class="filter-dialog__label">Shift Edge:</label>
                ${slider("shiftEdge", this.settings.shiftEdge, -100, 100, 1, "%")}
              </div>
            </div>

            <!-- Output Settings -->
            <div class="filter-dialog__section" data-section="output">
              <div class="filter-dialog__section-title">Output Settings</div>
              <div class="filter-dialog__row">
                <label class="filter-dialog__label">
                  <input type="checkbox" data-field="decontaminate" ${this.settings.decontaminate ? "checked" : ""}>
                  Decontaminate Colors
                </label>
              </div>
              <div class="filter-dialog__row">
                <label class="filter-dialog__label">Amount:</label>
                ${slider("decontaminateAmount", this.settings.decontaminateAmount, 0, 100, 1, "%", !this.settings.decontaminate)}
              </div>
              <div class="filter-dialog__row">
                <label class="filter-dialog__label">Output To:</label>
                <select class="filter-dialog__select" data-field="outputTo">
                  ${OUTPUT_TARGETS.map((m) =>
                    `<option value="${m.value}" ${m.value === this.settings.outputTo ? "selected" : ""}>${m.label}</option>`,
                  ).join("")}
                </select>
              </div>
            </div>

          </div>
          <div class="filter-dialog__actions">
            <button class="filter-dialog__btn" data-action="reset">Reset</button>
            <span style="flex:1"></span>
            <button class="filter-dialog__btn" data-action="cancel">Cancel</button>
            <button class="filter-dialog__btn filter-dialog__btn--primary" data-action="ok">OK</button>
          </div>
        </div>
      `;
      document.body.appendChild(overlay);
      this.overlay = overlay;
      this.wireEvents();

      const onKey = (e: KeyboardEvent) => {
        if (e.key === "Escape") {
          e.preventDefault();
          this.cancel();
          document.removeEventListener("keydown", onKey);
        }
      };
      document.addEventListener("keydown", onKey);
    });
  }

  close(): void {
    if (this.overlay) {
      this.overlay.remove();
      this.overlay = null;
    }
  }

  cancel(): void {
    const r = this.resolve;
    this.resolve = null;
    this.close();
    r?.(null);
  }

  accept(): void {
    const r = this.resolve;
    this.resolve = null;
    const settings = { ...this.settings };
    this.close();
    r?.({ settings });
  }

  reset(): void {
    this.settings = defaultSettings();
    if (!this.overlay) return;
    for (const key of Object.keys(this.settings) as (keyof SelectAndMaskSettings)[]) {
      const v = this.settings[key];
      const input = this.overlay.querySelector<HTMLInputElement | HTMLSelectElement>(`[data-field="${String(key)}"]`);
      const slider = this.overlay.querySelector<HTMLInputElement>(`[data-slider="${String(key)}"]`);
      if (input) {
        if (input instanceof HTMLInputElement && input.type === "checkbox") {
          input.checked = !!v;
        } else {
          input.value = String(v);
        }
      }
      if (slider) slider.value = String(v);
    }
  }

  /** Current snapshot of dialog settings (for tests and preview). */
  getSettings(): SelectAndMaskSettings {
    return { ...this.settings };
  }

  /** Programmatic setter (also used by tests). */
  setField<K extends keyof SelectAndMaskSettings>(key: K, value: SelectAndMaskSettings[K]): void {
    this.settings[key] = value;
    if (!this.overlay) return;
    const input = this.overlay.querySelector<HTMLInputElement | HTMLSelectElement>(`[data-field="${String(key)}"]`);
    const slider = this.overlay.querySelector<HTMLInputElement>(`[data-slider="${String(key)}"]`);
    if (input) {
      if (input instanceof HTMLInputElement && input.type === "checkbox") {
        input.checked = !!value;
      } else {
        input.value = String(value);
      }
    }
    if (slider) slider.value = String(value);
  }

  private wireEvents(): void {
    const o = this.overlay;
    if (!o) return;
    o.querySelector<HTMLButtonElement>('[data-action="cancel"]')?.addEventListener("click", () => this.cancel());
    o.querySelector<HTMLButtonElement>('[data-action="ok"]')?.addEventListener("click", () => this.accept());
    o.querySelector<HTMLButtonElement>('[data-action="reset"]')?.addEventListener("click", () => this.reset());
    o.addEventListener("click", (e) => {
      if (e.target === o) this.cancel();
    });

    // Wire each field by data-field attr.
    const wireText = (key: keyof SelectAndMaskSettings) => {
      const num = o.querySelector<HTMLInputElement>(`[data-field="${String(key)}"]`);
      const slider = o.querySelector<HTMLInputElement>(`[data-slider="${String(key)}"]`);
      const sync = (src: HTMLInputElement) => {
        const v = parseFloat(src.value);
        if (!Number.isFinite(v)) return;
        (this.settings as unknown as Record<string, unknown>)[key as string] = v;
        if (num && num !== src) num.value = String(v);
        if (slider && slider !== src) slider.value = String(v);
        this.onSettingsChanged();
      };
      num?.addEventListener("input", () => sync(num));
      slider?.addEventListener("input", () => sync(slider));
    };

    for (const key of ["radius", "smooth", "feather", "contrast", "shiftEdge", "decontaminateAmount"] as const) {
      wireText(key);
    }

    const viewMode = o.querySelector<HTMLSelectElement>('[data-field="viewMode"]');
    viewMode?.addEventListener("change", () => {
      this.settings.viewMode = viewMode.value as SelectAndMaskViewMode;
      this.onSettingsChanged();
    });
    const outputTo = o.querySelector<HTMLSelectElement>('[data-field="outputTo"]');
    outputTo?.addEventListener("change", () => {
      this.settings.outputTo = outputTo.value as SelectAndMaskOutput;
      this.onSettingsChanged();
    });
    const smart = o.querySelector<HTMLInputElement>('[data-field="smartRadius"]');
    smart?.addEventListener("change", () => {
      this.settings.smartRadius = smart.checked;
      this.onSettingsChanged();
    });
    const decon = o.querySelector<HTMLInputElement>('[data-field="decontaminate"]');
    decon?.addEventListener("change", () => {
      this.settings.decontaminate = decon.checked;
      // Toggle amount field disabled state.
      const amt = o.querySelector<HTMLInputElement>('[data-field="decontaminateAmount"]');
      const amtSlider = o.querySelector<HTMLInputElement>('[data-slider="decontaminateAmount"]');
      if (amt) amt.disabled = !decon.checked;
      if (amtSlider) amtSlider.disabled = !decon.checked;
      this.onSettingsChanged();
    });
  }

  private onSettingsChanged(): void {
    // Hook for live preview updates; runtime owners can swap in a real impl.
    // Default is a no-op so the dialog works in isolation/tests.
  }
}

// ── Local helpers ──────────────────────────────────────────────────────────

function slider(
  field: string,
  value: number,
  min: number,
  max: number,
  step: number,
  unit: string,
  disabled = false,
): string {
  const dis = disabled ? "disabled" : "";
  return `
    <div class="filter-dialog__slider-row">
      <input type="range" class="filter-dialog__slider" data-slider="${field}"
        min="${min}" max="${max}" step="${step}" value="${value}" ${dis}>
      <input type="number" class="filter-dialog__num" data-field="${field}"
        min="${min}" max="${max}" step="${step}" value="${value}" ${dis}>
      ${unit ? `<span class="filter-dialog__unit">${unit}</span>` : ""}
    </div>
  `;
}
